export const seoDomain = 'https://www.aucklandzoo.co.nz';
export const seoSiteName = 'Auckland Zoo';
export const seoDescription = 'Auckland Zoo is home to the largest collection of native and exotic animals in New Zealand, set in 17 hectares of lush parkland just minutes from central Auckland. There is lots to see and do all year, including events, eye-to-eye encounters, Zoom behind the scenes experiences and more!';
export const seoImagePath = `${seoDomain}/static/images/logo-new.svg`;

export const keywords = 'Auckland Zoo,Auckland,Zoo,Animals,Wildlife,The Zoo,conservation,zoo events,ZOOM,Friends of the Zoo,tours,behind the scenes tours,kiwi,tuatara,NZ animals,New Zealand animals,exotic animals,Auckland attractions,Things to do in Auckland';

export const ahrefsMetaContent = 'e88fd37b02fbd72645ae3ddd04f04199f0e8ba08b3ac4b5ad8f39def6969b69c';

const formatSchemaTime = (time) => {
    if (!time) return null;

    return time.split(':').slice(0, 2).join(':');
};

const NAMED_HTML_ENTITIES = {
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    apos: "'",
    nbsp: ' ',
    eacute: 'é',
    Eacute: 'É',
    rsquo: '\u2019',
    lsquo: '\u2018',
    rdquo: '\u201d',
    ldquo: '\u201c',
    mdash: '\u2014',
    ndash: '\u2013',
    hellip: '\u2026'
};

const decodeHtmlEntities = (value = '') => String(value).replace(
    /&(#x[0-9a-fA-F]+|#\d+|[a-zA-Z][a-zA-Z0-9]+);/g,
    (match, entity) => {
        if (entity[0] === '#') {
            const codePoint = entity[1] === 'x' || entity[1] === 'X' ?
                parseInt(entity.slice(2), 16) :
                parseInt(entity.slice(1), 10);

            if (Number.isNaN(codePoint)) {
                return match;
            }

            try {
                return String.fromCodePoint(codePoint);
            } catch (error) {
                return match;
            }
        }

        return Object.prototype.hasOwnProperty.call(NAMED_HTML_ENTITIES, entity) ?
            NAMED_HTML_ENTITIES[entity] :
            match;
    }
);

const stripHtml = (html = '') => decodeHtmlEntities(String(html).replace(/<[^>]+>/g, '')).trim();

const getValidFaqItems = (blocks = []) => blocks.reduce((items, block = {}) => {
    if (block.type !== 'faq' || !Array.isArray(block.data)) {
        return items;
    }

    return items.concat(block.data.filter((item = {}) => item.content && item.title));
}, []);

/**
 * Generates FAQ question entities for valid accordion content blocks.
 *
 * @param {array} blocks Page content blocks.
 *
 * @return {array} FAQ question entities.
 */
export function generateFAQMainEntity(blocks = []) {
    return getValidFaqItems(blocks).map(({ content, title }) => ({
        '@type': 'Question',
        name: title,
        acceptedAnswer: {
            '@type': 'Answer',
            text: stripHtml(content)
        }
    }));
}

export function generateFAQSchema({ url, name, description: pageDescription, blocks = [] }) {
    const mainEntity = generateFAQMainEntity(blocks);

    if (mainEntity.length === 0) {
        return null;
    }

    return {
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        url,
        name,
        ...(pageDescription ? { description: pageDescription } : {}),
        inLanguage: 'en-NZ',
        mainEntity
    };
}

export function buildWebsiteNode() {
    return {
        '@type': 'WebSite',
        '@id': `${seoDomain}/#website`,
        url: seoDomain,
        name: seoSiteName,
        description: seoDescription,
        inLanguage: 'en-NZ',
        publisher: {
            '@type': 'Organization',
            '@id': `${seoDomain}/#organization`,
            name: seoSiteName,
            url: seoDomain,
            logo: {
                '@type': 'ImageObject',
                url: seoImagePath
            }
        },
        potentialAction: {
            '@type': 'SearchAction',
            target: {
                '@type': 'EntryPoint',
                urlTemplate: `${seoDomain}/api/zoo/search?keywords={search_term_string}`
            },
            'query-input': 'required name=search_term_string'
        }
    };
}

export function generateWebsiteSchema() {
    return {
        '@context': 'https://schema.org',
        ...buildWebsiteNode()
    };
}

export function generateGlobalSchema(globalFields = {}) {
    const fields = globalFields || {};
    const opens = formatSchemaTime(fields.open_time);
    const closes = formatSchemaTime(fields.close_time);

    return {
        '@context': 'https://schema.org',
        '@graph': [
            {
                '@type': 'Organization',
                '@id': `${seoDomain}/#organization`,
                name: seoSiteName,
                url: seoDomain,
                logo: {
                    '@type': 'ImageObject',
                    url: seoImagePath
                },
                sameAs: [
                    'https://www.facebook.com/aucklandzoo',
                    'https://www.instagram.com/aucklandzoo/',
                    'https://www.youtube.com/user/aucklandzoo',
                    'https://twitter.com/aucklandzoo',
                    'https://www.wikidata.org/wiki/Q2665285'
                ],
                memberOf: [
                    {
                        '@type': 'Organization',
                        name: 'Zoo and Aquarium Association Australasia (ZAA)'
                    },
                    {
                        '@type': 'Organization',
                        name: 'World Association of Zoos and Aquariums (WAZA)'
                    },
                    {
                        '@type': 'Organization',
                        name: 'European Association of Zoos and Aquaria (EAZA)',
                        description: 'Associate Member'
                    }
                ]
            },
            {
                '@type': 'Zoo',
                '@id': `${seoDomain}/#zoo`,
                name: seoSiteName,
                url: seoDomain,
                description: seoDescription,
                telephone: '+64 9 360 3805',
                image: seoImagePath,
                address: [
                    {
                        '@type': 'PostalAddress',
                        name: 'Physical address',
                        streetAddress: '99 Motions Road',
                        addressLocality: 'Western Springs',
                        addressRegion: 'Auckland',
                        postalCode: '1022',
                        addressCountry: 'NZ'
                    },
                    {
                        '@type': 'PostalAddress',
                        name: 'Postal address',
                        streetAddress: 'Private Bag 78700',
                        addressLocality: 'Grey Lynn',
                        addressRegion: 'Auckland',
                        postalCode: '1245',
                        addressCountry: 'NZ'
                    }
                ],
                ...(opens && closes ? {
                    openingHoursSpecification: {
                        '@type': 'OpeningHoursSpecification',
                        dayOfWeek: [
                            'Monday',
                            'Tuesday',
                            'Wednesday',
                            'Thursday',
                            'Friday',
                            'Saturday',
                            'Sunday'
                        ],
                        opens,
                        closes
                    }
                } : {}),
                owner: {
                    '@type': 'Organization',
                    '@id': 'https://www.aucklandunlimited.com/#organization',
                    name: 'Auckland Unlimited',
                    url: 'https://www.aucklandunlimited.com/',
                    sameAs: [
                        'https://www.linkedin.com/company/auckland-unlimited',
                        'https://www.wikidata.org/wiki/Q55764471'
                    ],
                    logo: {
                        '@type': 'ImageObject',
                        url: 'https://aucklandunlimited.com/static/assets/images/tataki-auckland-unlimited-a-navy-rgb.26dbb9e1deac145cabbb.svg'
                    }
                }
            }
        ]
    };
}

export const globalSchema = generateGlobalSchema();

export function generatePageSchema({ url, name, description: pageDescription, image, breadcrumbs = [] }) {
    const graph = [
        {
            '@type': 'WebPage',
            '@id': `${url}#webpage`,
            url,
            name,
            ...(pageDescription ? { description: pageDescription } : {}),
            ...(image ? { image } : {}),
            ...(breadcrumbs.length > 0 ? { breadcrumb: { '@id': `${url}#breadcrumb` } } : {})
        }
    ];

    if (breadcrumbs.length > 0) {
        graph.push({
            '@type': 'BreadcrumbList',
            '@id': `${url}#breadcrumb`,
            itemListElement: breadcrumbs.map((item, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                name: item.heading,
                item: `${seoDomain}${item.path}`
            }))
        });
    }

    return {
        '@context': 'https://schema.org',
        '@graph': graph
    };
}

export function generateFAQPageSchema({ url, name, description: pageDescription, blocks = [] }) {
    const faqSchema = generateFAQSchema({ url, name, description: pageDescription, blocks });

    if (!faqSchema) {
        return null;
    }

    return {
        '@context': 'https://schema.org',
        ...faqSchema
    };
}

export function generateArticleSchema(attributes, url) {
    const image = (attributes && attributes.main_images && attributes.main_images.length && attributes.main_images[0]) ||
        attributes.thumbnail ||
        null;

    return {
        '@context': 'https://schema.org',
        '@type': attributes.type === 'news' ? 'NewsArticle' : 'Article',
        '@id': `${url}#article`,
        headline: attributes.name || '',
        url,
        ...(attributes.seo_description || attributes.short_description ? {
            description: attributes.seo_description || attributes.short_description
        } : {}),
        ...(image ? { image: [image] } : {}),
        ...(attributes.publish_at ? {
            datePublished: attributes.publish_at,
            dateModified: attributes.publish_at
        } : {}),
        ...(attributes.author ? {
            author: {
                '@type': 'Person',
                name: attributes.author
            }
        } : {}),
        publisher: { '@id': `${seoDomain}/#organization` },
        isPartOf: { '@id': `${url}#webpage` }
    };
}
